import type { CSSProperties } from 'react';
import React, { useState, useMemo, useCallback } from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import LazyLoad from 'react-lazyload';
import { theme } from 'Root/tailwind.config';
import { RATIOS, RATIO_VALUES } from 'Components/utils/ratios/ratios.utils';
import { getUrl } from 'Components/utils/media-server';
import type { Transformation } from 'Components/utils/media-server';
import { getOptimalWidthAndHeight } from 'Components/utils/media-server/dictionary';
import { ImageInvalid } from 'Components/frameworks/image-invalid/ImageInvalid';
import type { Displays, DisplayUnit } from './constants';
import { DISPLAY_VIEWPORT, DISPLAY_MOBILE, DISPLAY_PHABLET, DISPLAYS } from './constants';
const CONTAINER_MAX_WIDTH = theme.maxWidth.site;
const SIZE_AUTO = 'auto';
const DEFAULT_IMAGE_WIDTH = '100%';
const LOW_QUALITY = 70;
const BASE_QUALITY = 75;
const MAX_IMAGE_QUALITY = 100;
const DEFAULT_RATIO = 0.75;
const DEFAULT_IMAGE_FORMAT = '_AUTOFORMAT_';
const MEDIA_SERVER_CONFIG = {
  height: SIZE_AUTO,
  format: 'jpeg' as const,
  quality: LOW_QUALITY,
  gravity: 'center',
};
export type RatioDisplay = number | Displays;
type Size = {
  mediaquery: string | null;
  size: (ratio: number, unit?: DisplayUnit) => string;
};
type SizeXL = Size;
type SizeL = Size;
type SizeM = Size;
type SizeS = Size;

const getSizeRatio = (ratio: number, unit: DisplayUnit) => {
  if (unit === DISPLAY_VIEWPORT) {
    return `${100 * ratio}${unit}`;
  }

  return `${ratio}${unit}`;
};

const SIZES: [SizeXL, SizeL, SizeM, SizeS] = [
  {
    mediaquery: '(min-width: 64.01em)',
    size: (ratio) => `calc(${ratio} * ${CONTAINER_MAX_WIDTH})`,
  },
  {
    mediaquery: '(min-width: 40.01em)',
    size: (ratio, unit = DISPLAY_VIEWPORT) => getSizeRatio(ratio, unit),
  },
  {
    mediaquery: '(min-width: 20.01em)',
    size: (ratio, unit = DISPLAY_VIEWPORT) => getSizeRatio(ratio, unit),
  },
  {
    mediaquery: null,
    size: (ratio, unit = DISPLAY_VIEWPORT) => getSizeRatio(ratio, unit),
  },
];
type Format = {
  width: number;
  quality: number;
};
const FORMATS: Format[] = [
  {
    width: 320,
    quality: BASE_QUALITY,
  },
  {
    width: 640,
    quality: BASE_QUALITY,
  },
  {
    width: 800,
    quality: BASE_QUALITY,
  },
  {
    width: 1024,
    quality: BASE_QUALITY,
  },
];
const FULL_HD: Format[] = [
  {
    width: 1200,
    quality: LOW_QUALITY,
  },
  {
    width: 2000,
    quality: LOW_QUALITY,
  },
];

const isNumber = (val) => Number.isFinite(Number(val));

const getRatio = ({ dimensions, ratioKey }) => {
  if (
    dimensions?.w != null &&
    dimensions?.h != null &&
    isNumber(dimensions?.w) &&
    isNumber(dimensions?.h) &&
    Number(dimensions.w) > 0 &&
    Number(dimensions.h) > 0
  ) {
    return Number(dimensions.h) / Number(dimensions.w);
  }

  if (ratioKey) {
    return RATIO_VALUES[ratioKey] || DEFAULT_RATIO;
  }

  return DEFAULT_RATIO;
};

const clampDisplay = (ratio, unit) => {
  if (unit === DISPLAY_VIEWPORT) {
    return Math.max(Math.min(1, ratio), 0.15);
  }

  return ratio;
};

const getViewportRatios = (ratioDisplay) => {
  if (Array.isArray(ratioDisplay)) {
    return ratioDisplay.map(({ value, unit }) => ({
      value: clampDisplay(value, unit),
      unit,
    }));
  }

  const display = {
    value: clampDisplay(ratioDisplay, DISPLAY_VIEWPORT),
    unit: DISPLAY_VIEWPORT,
  };
  return [display, display, DISPLAY_MOBILE, DISPLAY_PHABLET];
};

const getWidth = ({ dimensions, dontChangeAnything }) => {
  if (dimensions?.w) {
    return dimensions.w;
  }

  if (dontChangeAnything) {
    return SIZE_AUTO;
  }

  return DEFAULT_IMAGE_WIDTH;
};

const getHeight = ({ dimensions, ratio, dontChangeAnything }) => {
  if (dimensions?.h) {
    return dimensions.h;
  }

  const width = getWidth({
    dimensions,
    dontChangeAnything,
  });

  if (isNumber(ratio) && isNumber(width)) {
    return Math.floor(Number(width) * Number(ratio));
  }

  return SIZE_AUTO;
};

const getMediaQueryHeight = (width, ratio) => {
  if (typeof width === 'number' && ratio) {
    return Math.round(width * ratio);
  }

  return SIZE_AUTO;
};

// what a life, Flow can't handle union type for values if passed object doesn't have strictly the same type
// { ex: string | number } can't validate { ex: string } or { ex: number }
export type Dimensions =
  | {
      w: string;
      h: string;
    }
  | {
      w: number;
      h: number;
    }
  | {
      w: 'auto';
      h: 'auto';
    }
  | {
      w: string;
      h: number;
    }
  | {
      w: number;
      h: string;
    }
  | {
      w: string;
      h: 'auto';
    }
  | {
      w: 'auto';
      h: string;
    }
  | {
      w: number;
      h: 'auto';
    }
  | {
      w: 'auto';
      h: number;
    };
type ImgProps = {
  altMsg?: string;
  className?: string;
  dimensions?: Dimensions;
  dontChangeAnything?: boolean;
  fullscreen?: boolean;
  lazyLoad?: boolean;
  noPlaceholderOnError?: boolean;
  onError?: () => void;
  onLoad?: () => void;
  ratioDisplay?: RatioDisplay;
  ratioKey?: string;
  style?: CSSProperties;
  transformation?: Transformation;
  url?: string;
};
export const Img = ({
  altMsg = '',
  className,
  dimensions,
  dontChangeAnything = false,
  fullscreen = false,
  lazyLoad = true,
  noPlaceholderOnError = false,
  onError = () => {},
  onLoad = () => {},
  ratioDisplay = DISPLAYS,
  // Ratio of displayed image (1 = 100% layout width, 0.5 = 50% layout width)
  ratioKey,
  style,
  transformation = 'crop',
  url,
}: ImgProps) => {
  const [hasError, setHasError] = useState(false);
  const handleOnError = useCallback(() => {
    setHasError(true);
    onError();
  }, []);
  const ratio = getRatio({
    dimensions,
    ratioKey,
  });
  const width = getWidth({
    dimensions,
    dontChangeAnything,
  });
  const height = getHeight({
    dimensions,
    ratio,
    dontChangeAnything,
  });
  const widthAuto = width === SIZE_AUTO;
  const heightAuto = height === SIZE_AUTO;
  const src = useMemo(() => {
    if (!url) {
      return null;
    }

    if (dontChangeAnything) {
      return url;
    }

    const w = isNumber(width) ? Number(width) : FORMATS.slice(-1)[0].width;
    const h = transformation === 'resize' ? SIZE_AUTO : height;
    return getUrl({ ...MEDIA_SERVER_CONFIG, url, width: w, height: h, transform: transformation });
  }, [url, width, height, transformation, dontChangeAnything]);
  const srcSet = useMemo(() => {
    if (!url) {
      return null;
    }

    if (dontChangeAnything) {
      return null;
    }

    const mapFormat = (format) => {
      const w = format.width;
      const h = transformation === 'resize' ? SIZE_AUTO : getMediaQueryHeight(w, ratio);
      const quality = Math.min(MAX_IMAGE_QUALITY, format.quality);
      const formatUrl = getUrl({
        ...MEDIA_SERVER_CONFIG,
        url: url,
        width: w,
        height: h,
        format: DEFAULT_IMAGE_FORMAT,
        quality,
        transform: transformation,
      });
      const { width: optimalWidth } = getOptimalWidthAndHeight(w, h);
      return `${formatUrl} ${optimalWidth}w`;
    };

    if (fullscreen) {
      return [...FORMATS, ...FULL_HD].map(mapFormat).join(', \n');
    }

    return FORMATS.map(mapFormat).join(', \n');
  }, [url, transformation, dontChangeAnything]);
  const viewportRatios = getViewportRatios(ratioDisplay);
  const sizes = useMemo(() => {
    if (dontChangeAnything) {
      return null;
    }

    if (fullscreen) {
      return '100vw';
    }

    return SIZES.map((size, idx) => {
      const { value, unit } = viewportRatios[idx];

      if (!size.mediaquery) {
        return size.size(value, unit);
      }

      return `${size.mediaquery} ${size.size(value, unit)}`;
    }).join(', \n');
  }, viewportRatios);
  const aspectRatio = useMemo(() => {
    if (dontChangeAnything) {
      return null;
    }

    if (RATIOS[ratioKey]) {
      return RATIOS[ratioKey];
    }

    if (
      width !== DEFAULT_IMAGE_WIDTH &&
      !widthAuto &&
      isNumber(width) &&
      !heightAuto &&
      isNumber(height)
    ) {
      return `${Number(width)} / ${Number(height)}`;
    }

    return null;
  }, [dontChangeAnything, width, widthAuto, height, heightAuto]);
  const imgStyle = {
    minHeight: '1px',
    aspectRatio,
    ...style,
  };

  if (!hasError && !src) {
    onError();
  }

  if ((!src || hasError) && noPlaceholderOnError) {
    return null;
  }

  if (!src || hasError) {
    return <ImageInvalid ratio={ratio} className="z-1" />;
  }

  const isClientSide = typeof window !== 'undefined';
  const browserSupportLazyLoading = isClientSide && 'loading' in HTMLImageElement.prototype;
  const useLazyLoad = isClientSide && !browserSupportLazyLoading && lazyLoad;
  const imgClassName = classnames(
    'max-h-full max-w-full',
    !dontChangeAnything && {
      'h-auto': heightAuto,
      'w-auto': widthAuto,
      'h-full': !heightAuto,
      'w-full': !widthAuto,
      'block object-cover object-center': true,
    },
    className,
    {
      hidden: hasError,
    },
  );
  const loading = lazyLoad === false ? 'eager' : 'lazy';
  const image = (
    <img
      data-testid="img-tag"
      alt={altMsg}
      className={imgClassName}
      style={imgStyle}
      loading={loading}
      sizes={sizes}
      srcSet={srcSet}
      src={src}
      width={width}
      height={height}
      onError={handleOnError}
      onLoad={onLoad}
    />
  );

  // see https://bugs.webkit.org/show_bug.cgi?id=196698
  if (useLazyLoad) {
    return (
      <LazyLoad height={height} debounce={100} once>
        {image}
      </LazyLoad>
    );
  }

  return image;
};
